import { useMemo } from "react";
import { queryOptions } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";

import { CurrencyContextProvider } from "@workspace/accounting/currencies";
import { ErrorComponent } from "@workspace/ui/components/error-component";
import {
	useVenueSettings,
	VenueSettingsProvider,
} from "@workspace/venue-management";
import {
	CheckInOutPinOnly,
	StaffPortalContextProvider,
	useStaffPortalContext,
} from "@workspace/venue-management-staff";
import { kioskApi } from "@workspace/venue-management-staff-terminal";
import {
	KioskContextProvider,
	useKioskContext,
} from "@workspace/venue-management-staff-terminal/kiosk";

import { TitleProvider } from "@/-app-contexts/title-context";
import { PageLoadingFull } from "@/components/page-loading";

export const Route = createFileRoute(
	"/_auth/_dashboard/$tenant/venues/$venueId",
)({
	component: RouteComponent,
	pendingComponent: PageLoadingFull,
	errorComponent: ErrorComponent,
	beforeLoad: async ({
		params: { venueId },
		context: { queryClient, tenant },
	}) => {
		const data = await queryClient.ensureQueryData(
			queryOptions({
				...kioskApi.getOptions(tenant, venueId),
				enabled: !!venueId,
			}),
		);

		return {
			venueId,
			venue: data.venueDetails,
		};
	},
});

function Content() {
	const { tenant, timeZone, venueId } = useVenueSettings();

	const { showCheckInOut, setShowCheckInOut } = useKioskContext();
	const { data, todayShift } = useStaffPortalContext();

	if (showCheckInOut) {
		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
				<CheckInOutPinOnly
					tenant={tenant}
					venueId={venueId}
					staffMembers={data.staffMembers}
					shifts={todayShift}
					onClose={() => setShowCheckInOut(false)}
					timeZone={timeZone}
				/>
			</div>
		);
	}

	return <Outlet />;
}

function RouteComponent() {
	const { venueId, venue, tenant } = Route.useRouteContext();

	const v = useMemo(() => venue, [venue]);

	return (
		<>
			<CurrencyContextProvider
				value={{
					tenant,
					venueId,
				}}
			>
				<VenueSettingsProvider
					tenant={tenant}
					venue={v}
					refetchInterval={10_000}
				>
					<TitleProvider defaultTitle="Dashboard">
						<KioskContextProvider>
							<StaffPortalContextProvider
								timeZone={v.timeZone}
								tenant={tenant}
								venueId={venueId}
							>
								<Content />
							</StaffPortalContextProvider>
						</KioskContextProvider>
					</TitleProvider>
				</VenueSettingsProvider>
			</CurrencyContextProvider>
		</>
	);
}
